import React, { useContext, useEffect, useState } from 'react';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgCargoItem from '@components/ringil3/icons/CargoItem.tsx';
import { InputNumber } from '@components/framework/input/InputNumber';
import InputSKU from '@components/ringil3/Input/InputSKU/InputSKU.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';

import { CapacityValue } from '@app/pages/customer/LineEdit/types.ts';

interface ComponentProps {
  value: CapacityValue;
  onChange: (value: CapacityValue) => void;
  readOnly?: boolean;
  errorMessage?: string;
}

/** "Kapacita" of a Linka: how many manipulation units one shipment carries ("1 přeprava = 4 FBU"). Becomes the laden factor. */
const CapacityTag: React.FC<ComponentProps> = ({ value, onChange, readOnly = false, errorMessage }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const [internal, setInternal] = useState<CapacityValue>(value ?? {});

  useEffect(() => setInternal(value ?? {}), [value?.ladenFactor, value?.cargoTemplateCode]);

  const display =
    value?.ladenFactor
      ? tr('CapacityTag.value', '1 přeprava = {{ladenFactor}} {{mu}}', {
          ladenFactor: value.ladenFactor,
          mu: value.cargoTemplateName ?? value.cargoTemplateCode ?? '',
        })
      : null;

  return (
    <ButtonTag
      label={tr('CapacityTag.title', 'Kapacita')}
      value={display}
      icon={<SvgCargoItem />}
      readonly={readOnly}
      errorMessage={errorMessage}
      autoClose
      onSave={onSuccess => {
        onChange(internal?.ladenFactor ? internal : { ladenFactor: null, cargoTemplateCode: null, cargoTemplateName: null });
        onSuccess();
      }}
      onClose={() => setInternal(value ?? {})}
      dataTestId={'line-capacity'}
    >
      <div className={'flex flex-col gap-2 min-w-[320px]'}>
        <Field label={tr('CapacityTag.ladenFactor', 'Počet jednotek na přepravu')}>
          <InputNumber
            value={internal?.ladenFactor ?? null}
            min={1}
            maxFractionDigits={0}
            onValueChange={e => setInternal(v => ({ ...v, ladenFactor: e.value }))}
          />
        </Field>
        <Field label={tr('CapacityTag.mu', 'Manipulační jednotka')}>
          <InputSKU
            value={internal?.cargoTemplateCode}
            companyId={loggedUser?.companyId}
            fullWidth
            onChange={code => setInternal(v => ({ ...v, cargoTemplateCode: code }))}
            onChangeCargoTemplate={ct => setInternal(v => ({ ...v, cargoTemplateCode: ct.code, cargoTemplateName: ct.name }))}
          />
        </Field>
      </div>
    </ButtonTag>
  );
};

export default CapacityTag;
